import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#EC4899'];

const ChartRenderer = ({ chartData }) => {
  if (!chartData || !chartData.type) return null;

  const { type, data, title, xKey, yKey, dataKey } = chartData;

  const containerStyle = {
    width: '100%',
    height: 300,
    marginTop: '1rem',
    marginBottom: '1rem',
  };

  const renderChart = () => {
    switch (type.toLowerCase()) {
      case 'bar':
        return (
          <ResponsiveContainer {...containerStyle}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey={xKey || 'name'} stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend />
              <Bar dataKey={yKey || dataKey || 'value'} fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer {...containerStyle}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey={xKey || 'name'} stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey={yKey || dataKey || 'value'} 
                stroke="#06B6D4" 
                strokeWidth={2}
                dot={{ fill: '#06B6D4' }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer {...containerStyle}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey={dataKey || 'value'}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return <p className="text-white/70">Unsupported chart type: {type}</p>;
    }
  };

  return (
    <div className="my-4 p-4 bg-black/30 rounded-lg border border-white/10">
      {title && <h4 className="text-white font-semibold mb-2 text-center">{title}</h4>}
      {renderChart()}
    </div>
  );
};

// Function to parse chart data from AI response
export const parseChartFromContent = (content) => {
  // Look for chart JSON in different formats
  const matches = [];
  
  // Pattern 1: ```chart {...} ```
  const chartRegex1 = /```chart\s*([\s\S]*?)```/g;
  let match;
  while ((match = chartRegex1.exec(content)) !== null) {
    try {
      const chartData = JSON.parse(match[1].trim());
      matches.push({ fullMatch: match[0], chartData });
    } catch (e) {
      console.error('Failed to parse chart data (pattern 1):', e);
    }
  }
  
  // Pattern 2: ```json {...} ``` with type field
  const chartRegex2 = /```json\s*(\{[\s\S]*?"type"\s*:\s*"(?:bar|line|pie)"[\s\S]*?\})\s*```/g;
  while ((match = chartRegex2.exec(content)) !== null) {
    try {
      const chartData = JSON.parse(match[1].trim());
      if (chartData.type && chartData.data) {
        matches.push({ fullMatch: match[0], chartData });
      }
    } catch (e) {
      console.error('Failed to parse chart data (pattern 2):', e);
    }
  }
  
  // Pattern 3: Just JSON object with type field (no code block)
  const chartRegex3 = /\{[^{}]*"type"\s*:\s*"(bar|line|pie)"[^{}]*"data"\s*:\s*\[[^\]]*\][^{}]*\}/g;
  while ((match = chartRegex3.exec(content)) !== null) {
    try {
      const chartData = JSON.parse(match[0]);
      if (chartData.type && chartData.data) {
        // Avoid duplicates
        const isDuplicate = matches.some(m => JSON.stringify(m.chartData) === JSON.stringify(chartData));
        if (!isDuplicate) {
          matches.push({ fullMatch: match[0], chartData });
        }
      }
    } catch (e) {
      // Try to clean up the JSON
      try {
        const cleaned = match[0].replace(/'/g, '"');
        const chartData = JSON.parse(cleaned);
        if (chartData.type && chartData.data) {
          matches.push({ fullMatch: match[0], chartData });
        }
      } catch (e2) {
        console.error('Failed to parse chart data (pattern 3):', e2);
      }
    }
  }
  
  return matches;
};

// Function to render content with charts
export const renderContentWithCharts = (content) => {
  const charts = parseChartFromContent(content);
  
  if (charts.length === 0) {
    return null;
  }
  
  let remainingContent = content;
  const elements = [];
  
  charts.forEach((chart, index) => {
    const parts = remainingContent.split(chart.fullMatch);
    if (parts[0]) {
      elements.push(<span key={`text-${index}`}>{parts[0]}</span>);
    }
    elements.push(<ChartRenderer key={`chart-${index}`} chartData={chart.chartData} />);
    remainingContent = parts.slice(1).join(chart.fullMatch);
  });
  
  if (remainingContent) {
    elements.push(<span key="text-final">{remainingContent}</span>);
  }
  
  return elements;
};

export default ChartRenderer;
